/* eslint-disable @typescript-eslint/naming-convention */
import { Injectable, OnDestroy } from '@angular/core';

import { BehaviorSubject, combineLatest, filter, Observable, Subscription } from 'rxjs';

import { ReportSeriesModel } from '../../Common/Reports/models/report-series.model';
import { LocalizationService } from '../../Core/Localization/services/localization.service';
import { JournodexRegionsService } from '../../Common/Journodex/services/journodex-regions.service';
import { monitoringFiltersEnum } from '../../Monitor/Shared/enums/monitoring-filters.enum';
import { VISeriesTypesEnum } from '../enums/vi-series-types.enum';

import { IndividualReportSeriesConfig } from './vi-reports-search.service';
import { ViRouteModel } from '../models/vi-route.model';
import { ViRoutingService } from './vi-routing.service';

export type VISeriesMap = Map<VISeriesTypesEnum, ReportSeriesModel>;
@Injectable()
export class ViChartsService implements OnDestroy {

    currentRoute: ViRouteModel;

    private subscriptions: Subscription[] = [];
    private seriesMap: VISeriesMap = new Map<VISeriesTypesEnum, ReportSeriesModel>;
    protected seriesMap$: BehaviorSubject<VISeriesMap> = new BehaviorSubject<VISeriesMap>(null);
    private chartConfig: IndividualReportSeriesConfig[];
    protected chartConfigReady$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    constructor(
        public routingService: ViRoutingService,
        public localizationService: LocalizationService,
        public journodexSearchService: JournodexRegionsService
    ) {
        this.subscriptions.push(
            combineLatest([
                this.chartConfigReady$.pipe(filter(x => !!x)),
                this.routingService.getCurrentRoute().pipe(filter(x => !!x && !!x.type))
            ]).subscribe(([ready, route]) => {
                this.currentRoute = route;
                this.setUpSeriesMap();
                this.seriesMap$.next(this.seriesMap);
            })
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(x => x.unsubscribe());
    }

    setChartConfig(config: IndividualReportSeriesConfig[]): void {
        this.chartConfig = config;
        this.chartConfigReady$.next(true);
    }

    setUpSeriesMap(): void {
        if (!this.chartConfig) {
            return;
        }
        this.chartConfig.forEach(config => this.seriesMap.set(
            (config.dynamicKey ? config.dynamicKey : config.type) as VISeriesTypesEnum, this.assignSeriesProperties(config)
        ));
    }

    assignSeriesProperties(config: IndividualReportSeriesConfig): ReportSeriesModel {
        const series = new ReportSeriesModel();
        if (config.label) {
            series.label = this.localizationService.transform(config.label);
        }
        if (config.metric) {
            series.metric = config.metric;
        }
        if (config.dynamicKey) {
            series.label = config.dynamicKey;
        }
        if (config.metrics && !config.dynamicKey) {
            series.metrics = config.metrics;
        }
        if (config.isPreviousPeriod) {
            series.mentions.isPreviousPeriod = config.isPreviousPeriod;
        }
        if (config.mentionProvider) {
            series.mentionProvider = config.mentionProvider;
        }
        if (config.facetGenerator) {
            series.mentions.facetGenerator = config.facetGenerator;
        }
        if (config.dataMapper) {
            series.mentions.dataMapper = config.dataMapper;
        }
        if (config.asyncDataMapper) {
            series.mentions.asyncDataMapper = config.asyncDataMapper;
        }
        if (config.mentionDataMapper) {
            series.mentions.mentionDataMapper = config.mentionDataMapper;
        }
        if (config.mentionRequestGenerator) {
            series.mentions.mentionRequestGenerator = config.mentionRequestGenerator;
        }
        if (config.mentionProvider) {
            series.mentions.mentionRequestGenerator = config.mentionRequestGenerator;
        }
        return series;
    }

    generateFilterErrorMessage(error: any): string {
        if (error?.filterTypes?.some( errorType =>
            errorType === monitoringFiltersEnum.regions
            || errorType === monitoringFiltersEnum.country
            || errorType === monitoringFiltersEnum.city
            || errorType === monitoringFiltersEnum.province
        )) {
            return this.localizationService.transform('Reports.filterError.regions');
        } else {
            throw error;
        }
    }

    getSeriesMap(): Observable<VISeriesMap> {
        return this.seriesMap$;
    }
}
